// Voxel grid stored as a sparse map of dense per-chunk arrays. Cell
// (ix, iy, iz) belongs to chunk (ix>>4, iy>>4, iz>>4); each chunk owns
// a Uint8Array of length CHUNK_SIZE³ = 4096 indexed by the cell's
// local (lx, ly, lz). 0 = empty.
//
// Why dense per chunk instead of a flat Map<cellKey, palette>: cell
// lookups are the hot path for the mesher (~220k per dense chunk
// rebuild between face + silhouette neighbor checks). With a flat
// Map even a numeric-keyed lookup pays a hash + bucket walk per
// call; per-chunk Uint8Array indexing is constant-time arithmetic
// with no allocation and lets the renderer iterate a chunk's
// cells as a contiguous array.
//
// Chunks only allocate when they get their first cell, and they free
// themselves when their last cell is removed (tracked via `count`).
// Mutations mark the owning chunk dirty plus any neighbor chunk on a
// shared boundary (those neighbors' exposed faces depend on the cell
// that changed). `consumeDirtyChunks()` returns and resets the set
// once per frame for the mesher.

import { recordChange } from "./history";

export const CELL_SIZE = 0.1;
// Edge length of one chunk in cells. 16³ = 4096 cells per chunk.
export const CHUNK_SIZE = 16;
export const CHUNK_AREA = CHUNK_SIZE * CHUNK_SIZE;
export const CHUNK_VOLUME = CHUNK_SIZE * CHUNK_AREA;

// Palette index. 0 is reserved for "empty". Real cell values are 1..255 to
// match MagicaVoxel's palette range, but the grid itself doesn't enforce
// the upper bound — the renderer just looks up whatever index is stored.
export type PaletteIndex = number;

// Chunk coordinate triples are packed into a single Number key so the
// `chunks` Map doesn't have to hash strings on every cell lookup.
// 16-bit per-axis range (-32768..32767) leaves the packed key well
// under Number.MAX_SAFE_INTEGER (2^53).
const KEY_BIAS = 32768;
const KEY_RANGE = 65536; // 2^16
const KEY_RANGE_SQ = KEY_RANGE * KEY_RANGE; // 2^32

function chunkLocal(c: number): number {
  return ((c % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
}

function chunkCoord(c: number): number {
  return Math.floor(c / CHUNK_SIZE);
}

function localIndex(ix: number, iy: number, iz: number): number {
  return (
    chunkLocal(ix) * CHUNK_AREA +
    chunkLocal(iy) * CHUNK_SIZE +
    chunkLocal(iz)
  );
}

export function chunkKey(cx: number, cy: number, cz: number): number {
  return (
    (cx + KEY_BIAS) * KEY_RANGE_SQ +
    (cy + KEY_BIAS) * KEY_RANGE +
    (cz + KEY_BIAS)
  );
}

export function parseChunkKey(k: number): [number, number, number] {
  const cz = (k % KEY_RANGE) - KEY_BIAS;
  const k2 = Math.floor(k / KEY_RANGE);
  const cy = (k2 % KEY_RANGE) - KEY_BIAS;
  const cx = Math.floor(k2 / KEY_RANGE) - KEY_BIAS;
  return [cx, cy, cz];
}

// Exported so the mesher's chunk rebuild can read the Uint8Array
// directly for in-chunk neighbor checks (the hot path during line
// + face emission), bypassing the per-call chunks Map lookup.
// Consumers should treat `cells` as immutable.
export interface ChunkStorage {
  cells: Uint8Array;
  count: number;
}

export class VoxelGrid {
  // Chunks present in the grid. Empty chunks are removed entirely so
  // iterating .chunks visits only non-empty regions.
  private chunks = new Map<number, ChunkStorage>();
  // Chunks whose mesh needs rebuilding since the last
  // consumeDirtyChunks() drain. Includes the chunk of the changed
  // cell plus any neighbor chunk whose mesh would be affected
  // (boundary cells).
  private dirty = new Set<number>();

  // Monotonic mutation counter. Derived views (selection outline, etc.)
  // cache the last value they synced against and skip work when it
  // hasn't changed. The chunked mesher uses dirtyChunks instead, but
  // version is still bumped so non-mesher consumers stay correct.
  version = 0;

  get(ix: number, iy: number, iz: number): PaletteIndex {
    const chunk = this.chunks.get(
      chunkKey(chunkCoord(ix), chunkCoord(iy), chunkCoord(iz)),
    );
    if (!chunk) return 0;
    return chunk.cells[localIndex(ix, iy, iz)];
  }

  has(ix: number, iy: number, iz: number): boolean {
    const chunk = this.chunks.get(
      chunkKey(chunkCoord(ix), chunkCoord(iy), chunkCoord(iz)),
    );
    if (!chunk) return false;
    return chunk.cells[localIndex(ix, iy, iz)] !== 0;
  }

  set(ix: number, iy: number, iz: number, p: PaletteIndex): void {
    if (p === 0) {
      this.delete(ix, iy, iz);
      return;
    }
    const cx = chunkCoord(ix);
    const cy = chunkCoord(iy);
    const cz = chunkCoord(iz);
    const ck = chunkKey(cx, cy, cz);
    let chunk = this.chunks.get(ck);
    if (!chunk) {
      chunk = { cells: new Uint8Array(CHUNK_VOLUME), count: 0 };
      this.chunks.set(ck, chunk);
    }
    const li = localIndex(ix, iy, iz);
    const prev = chunk.cells[li];
    if (prev === p) return;
    recordChange(ix, iy, iz, prev, p);
    chunk.cells[li] = p;
    if (prev === 0) chunk.count++;
    this.markChunkDirty(ix, iy, iz, cx, cy, cz);
    this.version++;
  }

  delete(ix: number, iy: number, iz: number): boolean {
    const cx = chunkCoord(ix);
    const cy = chunkCoord(iy);
    const cz = chunkCoord(iz);
    const ck = chunkKey(cx, cy, cz);
    const chunk = this.chunks.get(ck);
    if (!chunk) return false;
    const li = localIndex(ix, iy, iz);
    const prev = chunk.cells[li];
    if (prev === 0) return false;
    recordChange(ix, iy, iz, prev, 0);
    chunk.cells[li] = 0;
    chunk.count--;
    if (chunk.count === 0) this.chunks.delete(ck);
    this.markChunkDirty(ix, iy, iz, cx, cy, cz);
    this.version++;
    return true;
  }

  *entries(): IterableIterator<[number, number, number, PaletteIndex]> {
    for (const [ck, chunk] of this.chunks) {
      const [cx, cy, cz] = parseChunkKey(ck);
      const baseX = cx * CHUNK_SIZE;
      const baseY = cy * CHUNK_SIZE;
      const baseZ = cz * CHUNK_SIZE;
      const cells = chunk.cells;
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        for (let ly = 0; ly < CHUNK_SIZE; ly++) {
          for (let lz = 0; lz < CHUNK_SIZE; lz++) {
            const p = cells[lx * CHUNK_AREA + ly * CHUNK_SIZE + lz];
            if (p === 0) continue;
            yield [baseX + lx, baseY + ly, baseZ + lz, p];
          }
        }
      }
    }
  }

  // Iterate cells in the given chunk only. Yields nothing if the
  // chunk doesn't exist.
  *chunkEntries(
    cx: number,
    cy: number,
    cz: number,
  ): IterableIterator<[number, number, number, PaletteIndex]> {
    const chunk = this.chunks.get(chunkKey(cx, cy, cz));
    if (!chunk) return;
    const baseX = cx * CHUNK_SIZE;
    const baseY = cy * CHUNK_SIZE;
    const baseZ = cz * CHUNK_SIZE;
    const cells = chunk.cells;
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        for (let lz = 0; lz < CHUNK_SIZE; lz++) {
          const p = cells[lx * CHUNK_AREA + ly * CHUNK_SIZE + lz];
          if (p === 0) continue;
          yield [baseX + lx, baseY + ly, baseZ + lz, p];
        }
      }
    }
  }

  // Drain the dirty-chunk set. Mesher calls this once per frame; what
  // it gets back is the set of chunk keys whose meshes need rebuilding.
  consumeDirtyChunks(): Set<number> {
    const out = this.dirty;
    this.dirty = new Set();
    return out;
  }

  // The occupied region as an INCLUSIVE cell-coordinate AABB, chunk-granular
  // (cheap — O(allocated chunks), no per-cell scan). null when empty. The
  // raymarcher clips its DDA to this so it skips the empty space around a small
  // model instead of stepping the whole work volume.
  occupiedChunkBounds(): {
    x0: number; y0: number; z0: number;
    x1: number; y1: number; z1: number;
  } | null {
    if (this.chunks.size === 0) return null;
    let minCx = Infinity, minCy = Infinity, minCz = Infinity;
    let maxCx = -Infinity, maxCy = -Infinity, maxCz = -Infinity;
    for (const ck of this.chunks.keys()) {
      const [cx, cy, cz] = parseChunkKey(ck);
      if (cx < minCx) minCx = cx;
      if (cy < minCy) minCy = cy;
      if (cz < minCz) minCz = cz;
      if (cx > maxCx) maxCx = cx;
      if (cy > maxCy) maxCy = cy;
      if (cz > maxCz) maxCz = cz;
    }
    return {
      x0: minCx * CHUNK_SIZE, y0: minCy * CHUNK_SIZE, z0: minCz * CHUNK_SIZE,
      x1: maxCx * CHUNK_SIZE + CHUNK_SIZE - 1,
      y1: maxCy * CHUNK_SIZE + CHUNK_SIZE - 1,
      z1: maxCz * CHUNK_SIZE + CHUNK_SIZE - 1,
    };
  }

  // Hand the mesher a direct reference to the chunk's storage so its
  // per-cell loops can index `cells` arithmetically for the common
  // in-chunk neighbor checks instead of going through has()/get()
  // (which each do a chunks Map lookup).
  getChunkStorage(
    cx: number,
    cy: number,
    cz: number,
  ): ChunkStorage | undefined {
    return this.chunks.get(chunkKey(cx, cy, cz));
  }

  // Mirror-grid support for the off-thread mesher: insert/remove a chunk's raw
  // data by packed key directly, bypassing history and dirty-tracking. The
  // worker keeps a VoxelGrid mirror in sync this way (the main thread posts each
  // changed chunk) so buildChunkGeometry's neighbour lookups resolve. Not for
  // the editing grid — use set/delete there.
  setRawChunk(ck: number, cells: Uint8Array, count: number): void {
    this.chunks.set(ck, { cells, count });
  }
  removeRawChunk(ck: number): void {
    this.chunks.delete(ck);
  }

  // Read-side mirror of setRawChunk: iterate occupied chunks by packed key +
  // raw storage, for serialization (the persistence layer RLE-encodes each
  // chunk's `cells`). Only non-empty chunks exist in the map (they self-delete
  // when emptied), so every yielded chunk has count > 0.
  *rawChunks(): IterableIterator<[number, ChunkStorage]> {
    yield* this.chunks;
  }

  clear(): void {
    if (this.chunks.size === 0) return;
    // Mark every existing chunk dirty so the mesher tears down its
    // now-empty meshes on the next sync.
    for (const k of this.chunks.keys()) this.dirty.add(k);
    this.chunks.clear();
    this.version++;
  }

  // Re-dirty every existing chunk — used to fully re-feed a renderer that fell
  // out of sync (e.g. the mesher after the raymarcher was the active renderer).
  // Pair with a mesher reset to also drop chunks deleted meanwhile.
  markAllChunksDirty(): void {
    for (const k of this.chunks.keys()) this.dirty.add(k);
  }

  hasAnyCells(): boolean {
    return this.chunks.size > 0;
  }

  // Fast deep snapshot of all chunk data for whole-volume undo (Fill / Clear /
  // resize): clones each chunk's Uint8Array. Far cheaper than materializing
  // every cell via entries(); restored wholesale by restoreChunks. Independent
  // of the live grid, so it's safe to stash in the history stack.
  snapshotChunks(): Map<number, ChunkStorage> {
    const out = new Map<number, ChunkStorage>();
    for (const [k, c] of this.chunks) {
      out.set(k, { cells: c.cells.slice(), count: c.count });
    }
    return out;
  }

  // Replace all chunk data with a snapshot (re-cloned, so the stored snapshot
  // stays immutable under later edits), marking every chunk present in either
  // the old or new state dirty so the mesher reconciles. No per-cell history —
  // pair with a recorded snapshot diff.
  restoreChunks(snap: Map<number, ChunkStorage>): void {
    for (const k of this.chunks.keys()) this.dirty.add(k);
    this.chunks = new Map<number, ChunkStorage>();
    for (const [k, c] of snap) {
      this.chunks.set(k, { cells: c.cells.slice(), count: c.count });
      this.dirty.add(k);
    }
    this.version++;
  }

  // ── Bounded / incremental undo (the RLE-style "record only what changed")
  // path. Instead of cloning the whole grid for a bulk edit's undo, capture only
  // the chunks the edit TOUCHES, accumulated into a caller-owned map the first
  // time each is seen (a null value = the chunk was absent, so undo removes it).
  // For a deep erase on a solid block this is the difference between cloning the
  // whole grid every press and cloning a handful of chunks.
  captureChunksInto(
    map: Map<number, ChunkStorage | null>,
    x0: number, x1: number,
    y0: number, y1: number,
    z0: number, z1: number,
  ): void {
    if (x1 <= x0 || y1 <= y0 || z1 <= z0) return;
    const cx0 = chunkCoord(x0), cx1 = chunkCoord(x1 - 1);
    const cy0 = chunkCoord(y0), cy1 = chunkCoord(y1 - 1);
    const cz0 = chunkCoord(z0), cz1 = chunkCoord(z1 - 1);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const ck = chunkKey(cx, cy, cz);
          if (map.has(ck)) continue; // first-touch only — preserves the pre-edit state
          const c = this.chunks.get(ck);
          map.set(ck, c ? { cells: c.cells.slice(), count: c.count } : null);
        }
      }
    }
  }

  // Clone the current state of just the given chunk keys (null = absent) — the
  // "after" half of a bounded undo step (the keys come from a captureChunksInto map).
  snapshotChunksSubset(keys: Iterable<number>): Map<number, ChunkStorage | null> {
    const out = new Map<number, ChunkStorage | null>();
    for (const ck of keys) {
      const c = this.chunks.get(ck);
      out.set(ck, c ? { cells: c.cells.slice(), count: c.count } : null);
    }
    return out;
  }

  // Restore ONLY the chunks named in a bounded snapshot (set or remove each),
  // leaving the rest of the grid untouched. Marks the affected chunks + a
  // one-chunk border dirty so the mesher reconciles the seams.
  restoreChunksSubset(map: Map<number, ChunkStorage | null>): void {
    for (const [ck, snap] of map) {
      if (snap === null) this.chunks.delete(ck);
      else this.chunks.set(ck, { cells: snap.cells.slice(), count: snap.count });
      const [cx, cy, cz] = parseChunkKey(ck);
      for (let dx = -1; dx <= 1; dx++)
        for (let dy = -1; dy <= 1; dy++)
          for (let dz = -1; dz <= 1; dz++)
            this.dirty.add(chunkKey(cx + dx, cy + dy, cz + dz));
    }
    this.version++;
  }

  // Bulk-fill the half-open cell box [x0,x1) × [y0,y1) × [z0,z1) with palette p
  // (p must be non-zero), writing chunk arrays directly and marking touched
  // chunks (plus a one-chunk border, whose exposed faces see into the fill)
  // dirty once — no per-cell Map lookups or history. Pair with snapshot undo.
  // skipFilled=true leaves already-occupied cells untouched (the draw-box
  // "don't overwrite other voxels" rule), filling only empties.
  fillBox(
    x0: number, x1: number,
    y0: number, y1: number,
    z0: number, z1: number,
    p: PaletteIndex,
    skipFilled = false,
  ): void {
    if (x1 <= x0 || y1 <= y0 || z1 <= z0) return;
    const cx0 = chunkCoord(x0), cx1 = chunkCoord(x1 - 1);
    const cy0 = chunkCoord(y0), cy1 = chunkCoord(y1 - 1);
    const cz0 = chunkCoord(z0), cz1 = chunkCoord(z1 - 1);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const ck = chunkKey(cx, cy, cz);
          let chunk = this.chunks.get(ck);
          if (!chunk) {
            chunk = { cells: new Uint8Array(CHUNK_VOLUME), count: 0 };
            this.chunks.set(ck, chunk);
          }
          const bx = cx * CHUNK_SIZE, by = cy * CHUNK_SIZE, bz = cz * CHUNK_SIZE;
          const lx0 = Math.max(x0, bx) - bx, lx1 = Math.min(x1, bx + CHUNK_SIZE) - bx;
          const ly0 = Math.max(y0, by) - by, ly1 = Math.min(y1, by + CHUNK_SIZE) - by;
          const lz0 = Math.max(z0, bz) - bz, lz1 = Math.min(z1, bz + CHUNK_SIZE) - bz;
          const cells = chunk.cells;
          for (let lx = lx0; lx < lx1; lx++) {
            for (let ly = ly0; ly < ly1; ly++) {
              const rowBase = lx * CHUNK_AREA + ly * CHUNK_SIZE;
              for (let lz = lz0; lz < lz1; lz++) {
                const li = rowBase + lz;
                if (cells[li] === 0) {
                  cells[li] = p;
                  chunk.count++;
                } else if (!skipFilled) {
                  cells[li] = p;
                }
              }
            }
          }
        }
      }
    }
    for (let cx = cx0 - 1; cx <= cx1 + 1; cx++) {
      for (let cy = cy0 - 1; cy <= cy1 + 1; cy++) {
        for (let cz = cz0 - 1; cz <= cz1 + 1; cz++) {
          this.dirty.add(chunkKey(cx, cy, cz));
        }
      }
    }
    this.version++;
  }

  // Restore the half-open cell box to the state held in a chunk snapshot — used
  // by box-erase's retract (drag back to un-erase). Each cell is set to its
  // value in `snap` (0 / absent chunk = was empty), updating counts and marking
  // the region + a one-chunk border dirty. Bulk; no history.
  restoreBoxFrom(
    snap: Map<number, ChunkStorage | null>,
    x0: number, x1: number,
    y0: number, y1: number,
    z0: number, z1: number,
  ): void {
    if (x1 <= x0 || y1 <= y0 || z1 <= z0) return;
    const cx0 = chunkCoord(x0), cx1 = chunkCoord(x1 - 1);
    const cy0 = chunkCoord(y0), cy1 = chunkCoord(y1 - 1);
    const cz0 = chunkCoord(z0), cz1 = chunkCoord(z1 - 1);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const ck = chunkKey(cx, cy, cz);
          const src = snap.get(ck); // original chunk (undefined = was empty)
          let chunk = this.chunks.get(ck);
          const bx = cx * CHUNK_SIZE, by = cy * CHUNK_SIZE, bz = cz * CHUNK_SIZE;
          const lx0 = Math.max(x0, bx) - bx, lx1 = Math.min(x1, bx + CHUNK_SIZE) - bx;
          const ly0 = Math.max(y0, by) - by, ly1 = Math.min(y1, by + CHUNK_SIZE) - by;
          const lz0 = Math.max(z0, bz) - bz, lz1 = Math.min(z1, bz + CHUNK_SIZE) - bz;
          for (let lx = lx0; lx < lx1; lx++) {
            for (let ly = ly0; ly < ly1; ly++) {
              const rowBase = lx * CHUNK_AREA + ly * CHUNK_SIZE;
              for (let lz = lz0; lz < lz1; lz++) {
                const li = rowBase + lz;
                const orig = src ? src.cells[li] : 0;
                const cur = chunk ? chunk.cells[li] : 0;
                if (orig === cur) continue;
                if (orig !== 0) {
                  if (!chunk) {
                    chunk = { cells: new Uint8Array(CHUNK_VOLUME), count: 0 };
                    this.chunks.set(ck, chunk);
                  }
                  if (chunk.cells[li] === 0) chunk.count++;
                  chunk.cells[li] = orig;
                } else if (chunk) {
                  chunk.cells[li] = 0;
                  chunk.count--;
                }
              }
            }
          }
          if (chunk && chunk.count === 0) this.chunks.delete(ck);
        }
      }
    }
    for (let cx = cx0 - 1; cx <= cx1 + 1; cx++) {
      for (let cy = cy0 - 1; cy <= cy1 + 1; cy++) {
        for (let cz = cz0 - 1; cz <= cz1 + 1; cz++) {
          this.dirty.add(chunkKey(cx, cy, cz));
        }
      }
    }
    this.version++;
  }

  // Bulk-erase the half-open cell box [x0,x1) × [y0,y1) × [z0,z1): zero every
  // occupied cell in it, writing chunk arrays directly with no per-cell Map
  // lookups or history. Pair with snapshot undo (the box-erase commit).
  clearBox(
    x0: number, x1: number,
    y0: number, y1: number,
    z0: number, z1: number,
  ): void {
    if (x1 <= x0 || y1 <= y0 || z1 <= z0) return;
    const cx0 = chunkCoord(x0), cx1 = chunkCoord(x1 - 1);
    const cy0 = chunkCoord(y0), cy1 = chunkCoord(y1 - 1);
    const cz0 = chunkCoord(z0), cz1 = chunkCoord(z1 - 1);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const ck = chunkKey(cx, cy, cz);
          const chunk = this.chunks.get(ck);
          if (!chunk) continue;
          const bx = cx * CHUNK_SIZE, by = cy * CHUNK_SIZE, bz = cz * CHUNK_SIZE;
          const lx0 = Math.max(x0, bx) - bx, lx1 = Math.min(x1, bx + CHUNK_SIZE) - bx;
          const ly0 = Math.max(y0, by) - by, ly1 = Math.min(y1, by + CHUNK_SIZE) - by;
          const lz0 = Math.max(z0, bz) - bz, lz1 = Math.min(z1, bz + CHUNK_SIZE) - bz;
          const cells = chunk.cells;
          for (let lx = lx0; lx < lx1; lx++) {
            for (let ly = ly0; ly < ly1; ly++) {
              const rowBase = lx * CHUNK_AREA + ly * CHUNK_SIZE;
              for (let lz = lz0; lz < lz1; lz++) {
                const li = rowBase + lz;
                if (cells[li] !== 0) {
                  cells[li] = 0;
                  chunk.count--;
                }
              }
            }
          }
          if (chunk.count === 0) this.chunks.delete(ck);
        }
      }
    }
    for (let cx = cx0 - 1; cx <= cx1 + 1; cx++) {
      for (let cy = cy0 - 1; cy <= cy1 + 1; cy++) {
        for (let cz = cz0 - 1; cz <= cz1 + 1; cz++) {
          this.dirty.add(chunkKey(cx, cy, cz));
        }
      }
    }
    this.version++;
  }

  private markChunkDirty(
    ix: number,
    iy: number,
    iz: number,
    cx: number,
    cy: number,
    cz: number,
  ): void {
    this.dirty.add(chunkKey(cx, cy, cz));
    // Boundary cells: the neighbor chunk on the opposite side of the
    // shared face needs to re-emit too, since one of its cells'
    // exposed-face checks looks at this cell.
    const lx = chunkLocal(ix);
    const ly = chunkLocal(iy);
    const lz = chunkLocal(iz);
    if (lx === 0) this.dirty.add(chunkKey(cx - 1, cy, cz));
    if (lx === CHUNK_SIZE - 1) this.dirty.add(chunkKey(cx + 1, cy, cz));
    if (ly === 0) this.dirty.add(chunkKey(cx, cy - 1, cz));
    if (ly === CHUNK_SIZE - 1) this.dirty.add(chunkKey(cx, cy + 1, cz));
    if (lz === 0) this.dirty.add(chunkKey(cx, cy, cz - 1));
    if (lz === CHUNK_SIZE - 1) this.dirty.add(chunkKey(cx, cy, cz + 1));
  }
}
